import { HocuspocusProvider } from "@hocuspocus/provider";
import { Schema } from "@tiptap/pm/model";
import { JSONContent, useEditor } from "@tiptap/react";
import { Edge, Node } from "reactflow";
import { prosemirrorJSONToYDoc, prosemirrorJSONToYXmlFragment } from "y-prosemirror";
import * as Y from "yjs";

import { loadExtensions } from "@/extensions";
import { SemanticPaper } from "@/types";

export const bulletList = (items: JSONContent[]): JSONContent => {
  return {
    type: "bulletList",
    content: items,
  };
};

export const listItem = (blockId: string, text?: string): JSONContent => {
  return {
    type: "listItem",
    attrs: { blockId },
    content: [{ type: "paragraph", content: [{ type: "text", text: text ?? "" }] }],
  };
};

export const setNodePageContent = (content: JSONContent, roomName: string, schema: Schema) => {
  const ydoc = new Y.Doc({ guid: roomName });
  new HocuspocusProvider({
    url: import.meta.env.VITE_HOCUSPOCUS_URL,
    name: roomName,
    document: ydoc,
  });
  const xml = ydoc?.getXmlFragment("default");
  prosemirrorJSONToYXmlFragment(schema, content, xml);
};

export const setNodePageJson = (content: JSONContent, roomName: string, schema: Schema) => {
  const ydoc = new Y.Doc({ guid: roomName });
  new HocuspocusProvider({
    url: import.meta.env.VITE_HOCUSPOCUS_URL,
    name: roomName,
    document: ydoc,
  });
  const xml = ydoc?.getXmlFragment("default");
  prosemirrorJSONToYXmlFragment(
    schema,
    {
      type: "doc",
      content,
    },
    xml
  );

  // This waits for the page and syncs it when it's ready
  // meaning it's replaced instead of appended to
  // provider?.on("sync", () => {
  //   console.log("Synced! Saving ", roomName);
  //   const xml = ydoc?.getXmlFragment("default");
  //   xml.delete(0, xml.length);
  //   prosemirrorJSONToYXmlFragment(
  //     schema,
  //     {
  //       type: "doc",
  //       content,
  //     },
  //     xml
  //   );
  // });
};

export const removeContentTokens = (content: string) => {
  return content.replace(/\(\|[A-Za-z0-9_-]+\|[01]{1}\|\): /g, "").replace(/\`\`\`/g, "");
};

export const addMetaToMarkdown = (markdown: string, nodes: Node[], edges: Edge[]) => {
  const nodeIds = nodes.map((node) => node.id);
  markdown.split("\n").forEach((line) => {
    const match = line.match(/\(\|([A-Za-z0-9_-]+)\|[01]{1}\|\): /);
    if (match) {
      const id = match[1];

      // TODO: Sending connections to LLM needs to be improved
      const foundEdges = edges
        .filter((edge) => nodeIds.includes(edge.source) && nodeIds.includes(edge.target))
        .filter((edge) => edge.source == id || edge.target == id);
      let toAppend = "";
      if (foundEdges.length) {
        const ids = foundEdges.map((edge) => (edge.source == id ? edge.target : edge.source));
        toAppend += ` (Connects to: ${ids.join(", ")}) `;
      }
      const foundNode = nodes.find((node) => node.id == id);
      if (foundNode) {
        if (foundNode?.data?.borderColor) {
          toAppend += ` (Color: ${foundNode?.data?.borderColor}) `;
        }
      }

      if (toAppend) {
        markdown = markdown.replace(line, `${line} ${toAppend}`);
      }
    }
  });
  return markdown;
};

export const handleApiError = (error: unknown) => {
  if (
    !(
      `${error}`.includes("The user aborted a request.") ||
      `${error}`.includes("AbortError") ||
      `${error}`.includes("network error")
    )
  ) {
    alert(error);
  }
};

export function semanticPaperToTipTapJSON(paper: SemanticPaper): JSONContent {
  const content = [
    {
      type: "heading",
      attrs: { level: 1 },
      content: [{ type: "text", text: paper.title }],
    },
    {
      type: "paragraph",
      content: [{ type: "text", text: `Year: ${paper.year}` }],
    },
    {
      type: "paragraph",
      content: [
        {
          type: "text",
          text: `Authors: ${paper.authors.map((author) => author.name).join(", ")}`,
        },
      ],
    },
    {
      type: "paragraph",
      content: [
        {
          type: "text",
          text: `Open Access: ${paper.isOpenAccess ? "Yes" : "No"}`,
        },
      ],
    },
  ];

  if (paper?.openAccessPdf?.url) {
    content.push({
      type: "paragraph",
      content: [
        {
          type: "text",
          text: "Open Access PDF: ",
        },
        {
          type: "text",
          marks: [
            {
              type: "link",
              attrs: { href: paper?.openAccessPdf?.url ?? "" },
            },
          ],
          text: paper.openAccessPdf.url,
        },
      ],
    });
  }

  content.push({
    type: "paragraph",
    content: [
      {
        type: "text",
        text: "Abstract: ",
      },
      {
        type: "text",
        text: paper?.abstract ?? "No abstract available",
      },
    ],
  });

  return { type: "doc", content };
}
